// =============================================================================
// MeuDriver — Landing Testimonials + Instructor CTA (Sections 6 + 7)
// =============================================================================

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Car, Star } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay } }),
}
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }
const viewportOnce = { once: true, margin: '-80px' }

const TESTIMONIALS = [
  {
    name: 'Juliana Fernandes',
    city: 'São Paulo, SP',
    rating: 5,
    text: 'Aprendi a dirigir com 55 anos! Sempre tive medo, mas o instrutor Carlos foi incrível — paciente, didático e nunca me pressionou. Em 10 aulas já estava confiante para tirar minha CNH.',
    initials: 'JF',
    gradient: 'from-brand-500 to-brand-700',
  },
  {
    name: 'Pedro Almeida',
    city: 'Rio de Janeiro, RJ',
    rating: 5,
    text: 'Depois de reprovar no exame prático duas vezes, a Ana me ajudou a entender onde estava errando. Focamos nos pontos críticos e passei na terceira tentativa. Valeu muito!',
    initials: 'PA',
    gradient: 'from-teal-500 to-teal-700',
  },
  {
    name: 'Beatriz Costa',
    city: 'Belo Horizonte, MG',
    rating: 5,
    text: 'Precisava aprender câmbio manual para o trabalho. Em 6 aulas com o Marcos já conseguia dirigir sem trancos. Plataforma fácil de usar e pagamento sem complicação.',
    initials: 'BC',
    gradient: 'from-orange-400 to-orange-600',
  },
]

export function LandingTestimonials() {
  return (
    <section className="bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5 }} className="text-center mb-12">
          <Badge variant="success" className="mb-3 text-sm px-3 py-1">Depoimentos</Badge>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl tracking-tight">O que nossos alunos dizem</h2>
          <p className="mt-3 text-gray-500">Histórias reais de quem aprendeu a dirigir com a MeuDriver.</p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce} className="grid gap-5 sm:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.name} variants={fadeUp} custom={i * 0.1}>
              <div className="relative h-full rounded-2xl border border-gray-100 bg-white shadow-soft hover:shadow-card transition-shadow duration-300 p-6 flex flex-col gap-4 overflow-hidden">
                <span className="absolute top-3 right-4 text-8xl font-black leading-none text-gray-50 select-none pointer-events-none" aria-hidden>"</span>
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <Star key={si} className="h-4 w-4 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <blockquote className="text-sm text-gray-600 leading-relaxed flex-1 relative">{t.text}</blockquote>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.city}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export function LandingCTA() {
  return (
    <section className="bg-slate-950 py-20 sm:py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-700/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-teal-900/30 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      </div>

      <div className="relative mx-auto max-w-3xl px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.55 }} className="flex flex-col items-center gap-6">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-teal-500 shadow-xl">
            <Car className="h-8 w-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl tracking-tight">Você é instrutor de direção?</h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed max-w-xl mx-auto">
              Cadastre-se na MeuDriver e amplie sua base de alunos. Receba pagamentos com segurança e gerencie sua agenda online.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button asChild size="lg" variant="orange" className="px-8 text-base shadow-orange/20 font-semibold">
              <Link to="/seja-instrutor">
                Começar agora
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" className="px-8 text-base bg-white/10 text-white border border-white/15 hover:bg-white/20 hover:border-white/25 transition-all">
              <Link to="/instrutores">Conhecer a plataforma</Link>
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 pt-5 border-t border-white/8 w-full">
            {[
              { label: 'Comissão justa', value: '15%' },
              { label: 'Pagamento rápido', value: '48h' },
              { label: 'Suporte dedicado', value: '7 dias' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <p className="text-2xl font-extrabold text-teal-400">{item.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
